import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export type ScoreType = "fci" | "credit" | "mastery_tier" | "topic_mastery";

export interface ScoreChange {
  id: string;
  score_type: ScoreType;
  before_value: number | null;
  after_value: number | null;
  delta: number | null;
  reason: string;
  source_action: string | null;
  created_at: string;
}

/**
 * Fetches the most recent score-change entries from `score_change_log` for the
 * current user (or, if userId is provided, for that user — RLS allows parents).
 */
export function useScoreChanges(scoreType?: ScoreType, limit = 10, userId?: string) {
  const { user } = useAuth();
  const [changes, setChanges] = useState<ScoreChange[]>([]);
  const [loading, setLoading] = useState(true);

  const targetId = userId ?? user?.id ?? null;

  const fetchChanges = useCallback(async () => {
    if (!targetId) { setChanges([]); setLoading(false); return; }
    let q = supabase
      .from("score_change_log")
      .select("*")
      .eq("user_id", targetId)
      .order("created_at", { ascending: false })
      .limit(limit);
    if (scoreType) q = q.eq("score_type", scoreType);
    const { data } = await q;
    setChanges(((data) || []) as ScoreChange[]);
    setLoading(false);
  }, [targetId, scoreType, limit]);

  useEffect(() => { void fetchChanges(); }, [fetchChanges]);

  return { changes, loading, refetch: fetchChanges };
}
